import { SettingsOutlined } from "@mui/icons-material";
import { FormControl, IconButton } from "@mui/material";
import {
  type ChangeEventHandler,
  type FormEvent,
  type KeyboardEventHandler,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

import ExperienceSettingsModal from "@/VisualBuilder/components/ExperienceSettingsDialog/ExperienceSettingsModal";
import { useUrlParams } from "@/hooks";
import { PencilRed } from "@/icons";
import { experienceMediaItemsSelector } from "@/store/v2/experienceMediaItems/selectors";
import { validateExperienceTitle } from "@/utils/validator";

import * as S from "./styled";

interface ExperienceTitleProps {
  readonly handleTitleUpdate: (title: string, label: string) => void;
  readonly disabled?: boolean;
  readonly label?: string;
  readonly title?: string;
}

export default function ExperienceTitle(props: ExperienceTitleProps) {
  const { disabled, handleTitleUpdate, label = "", title = "" } = props;

  const [createTitleValue, setCreateTitleValue] = useState(title || "");
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTagError, setNewTagError] = useState<string | undefined>("");
  const [showSettings, setShowSettings] = useState(false);

  const MAX_TITLE_LENGTH = 62;

  const params = useUrlParams();

  const { experienceId, propertyId } = params;

  const { experienceMediaItems = {} } = useSelector(experienceMediaItemsSelector(propertyId, experienceId));
  const experienceMediaItemsLength = Object.keys(experienceMediaItems).length;

  const isSettingsDisabled = experienceMediaItemsLength < 1;

  const timerId = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (timerId.current) {
        clearTimeout(timerId.current);
      }
    };
  }, []);

  useEffect(() => {
    setCreateTitleValue(title);
  }, [title]);

  const tagValue = createTitleValue;

  const validate = (value: string) => {
    const [message] = validateExperienceTitle(value);
    return message;
  };

  const doSubmit = async () => {
    setEditingTitle(false);
    if (timerId.current) {
      clearTimeout(timerId.current);
    }

    const errorMessage = validate(tagValue);
    setNewTagError(errorMessage);

    const titleTrimmed = title.trim();
    const newTitleTrimmed = tagValue.trim();

    if (!errorMessage) {
      if (titleTrimmed !== newTitleTrimmed) {
        handleTitleUpdate(newTitleTrimmed, label);
      }

      setCreateTitleValue(newTitleTrimmed);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    await doSubmit();
  };

  const handleBlur = async () => {
    const newTitle = tagValue.trim();

    if (!newTitle) {
      setCreateTitleValue(title);
      setNewTagError("");
    } else {
      await doSubmit();
    }
  };

  const shouldBlur: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    if (e.key === "Enter") {
      (e.target as HTMLElement).blur();
    }
  };

  const handleChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (e) => {
    e.persist();
    setCreateTitleValue(e.target.value);
    if (timerId.current) {
      clearTimeout(timerId.current);
    }

    if (!editingTitle) {
      setEditingTitle(true);
      setNewTagError(undefined);
    }

    timerId.current = setTimeout(() => {
      const errorMessage = validate(e.target.value);
      setNewTagError(errorMessage);
    }, 300);
  };

  const handleCloseDialog = () => {
    setShowSettings(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormControl>
        <S.TitleInput
          disabled={disabled}
          onKeyDown={shouldBlur}
          margin="dense"
          value={createTitleValue || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Title"
          inputProps={{
            maxLength: MAX_TITLE_LENGTH,
            style: {
              backgroundColor: "transparent",
              padding: "3px 0",
            },
          }}
          endAdornment={
            <>
              <S.TitleInputAdornment $editingTitle={editingTitle} position="end">
                <IconButton aria-label="update name" onClick={handleSubmit} size="small">
                  <PencilRed htmlColor="#fff" />
                </IconButton>
              </S.TitleInputAdornment>
              <S.SettingsIconButton
                data-testid="experience-settings-gear"
                disabled={isSettingsDisabled}
                aria-label="experience settings"
                onClick={() => setShowSettings(true)}
                size="small"
              >
                <SettingsOutlined fontSize="small" />
              </S.SettingsIconButton>
              <ExperienceSettingsModal open={showSettings} onCloseDialog={handleCloseDialog} />
            </>
          }
        />
        <S.ErrorHelperText error={!!newTagError}>{newTagError}</S.ErrorHelperText>
      </FormControl>
    </form>
  );
}
